/**
 * The bare domain bcportal.app (and www.) isn't a portal. It shows a neutral "Sign in to your
 * portal" page (RootSite) instead of sending everyone into the demo tenant, as it used to.
 * Every portal lives at <subdomain>.bcportal.app.
 */

/** Hosts that show the platform's own landing page rather than a tenant portal. */
export const ROOT_SITE_HOSTS: ReadonlySet<string> = new Set(['bcportal.app', 'www.bcportal.app']);

/** The portal domain every tenant address ends with. */
export const PORTAL_DOMAIN = 'bcportal.app';

/** The public demo portal (the old root redirect target). */
export const DEMO_PORTAL_URL = 'https://goodstuff365.bcportal.app/Cronus/';

export function isRootSiteHost(hostname: string): boolean {
  return ROOT_SITE_HOSTS.has(hostname.toLowerCase());
}

// Same rule as the Worker's subdomain validation: lowercase letters, digits and single hyphens.
const SUBDOMAIN_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export type PortalAddressResult = { ok: true; subdomain: string; url: string } | { ok: false; error: string };

/**
 * Turn what someone typed ("acme", "Acme.bcportal.app", "https://acme.bcportal.app/login")
 * into a portal subdomain and the address to open. Never returns an address outside bcportal.app.
 */
export function parsePortalAddress(input: string): PortalAddressResult {
  let v = input.trim().toLowerCase();
  if (!v) return { ok: false, error: 'Enter your portal name, for example yourcompany.' };
  v = v.replace(/^[a-z]+:\/\//, '');          // drop https://
  v = v.split(/[/?#]/)[0];                     // drop any path or query
  if (v.endsWith('.' + PORTAL_DOMAIN)) v = v.slice(0, -(PORTAL_DOMAIN.length + 1));
  if (v === PORTAL_DOMAIN || v === 'www' || v === '') {
    return { ok: false, error: 'Enter the first part of your portal address, before .bcportal.app.' };
  }
  if (v.length > 63 || !SUBDOMAIN_RE.test(v)) {
    return { ok: false, error: 'Use only letters, numbers and hyphens, for example your-company.' };
  }
  return { ok: true, subdomain: v, url: `https://${v}.${PORTAL_DOMAIN}/` };
}
